import { useState } from "react";
import { usePaginationCtx } from "../context/PaginationContext";
import {
  ITripDateRange,
  defaultPastDateRange,
  defaultUpcomingDateRange,
} from "../types/TripTypes";
import { RangeValue } from "rc-picker/lib/interface.d";
import { Moment } from "moment";
import { Tabs, defaultTripTypes } from "../types/TripPaginationFilterTypes";

const defaultDateRange = {
  [Tabs.UPCOMING]: {
    startDate: defaultUpcomingDateRange().startDate,
    endDate: defaultUpcomingDateRange().endDate,
  },
  [Tabs.PAST]: {
    startDate: defaultPastDateRange().startDate,
    endDate: defaultPastDateRange().endDate,
  },
};

export const useServiceUserProfileFilterActions = (
  setIsOpenFilter: Function
) => {
  const [checkedTripTypes, setCheckedTripTypes] = useState<{
    [tab: string]: Set<string>;
  }>(defaultTripTypes);
  const [selectedDateRanges, setSelectedDateRanges] = useState<{
    [Tab: string]: ITripDateRange;
  }>(defaultDateRange);

  const serviceTypesOptions: string[] = ["All", "Ride"];

  const {
    activeTabCtx,
    tripTypesCtx,
    upcomingDateRangeCtx,
    pastDateRangeCtx,
  } = usePaginationCtx();

  const [activeTab] = activeTabCtx;
  const [, setTripTypes] = tripTypesCtx;
  const [, setPastDateRange] = pastDateRangeCtx;
  const [, setUpcomingDateRange] = upcomingDateRangeCtx;

  const handleFilterDateRange = (
    dates: RangeValue<Moment>,
    dateStrings: string[]
  ) => {
    setSelectedDateRanges((previousSelectedDateRanges) => ({
      ...previousSelectedDateRanges,
      [activeTab]: {
        startDate: dateStrings[0],
        endDate: dateStrings[1],
      },
    }));
  };

  const isActiveTabChecked = (option: string) =>
    checkedTripTypes[activeTab]?.has(option);

  const handleTypesClick = (option: string): void => {
    setCheckedTripTypes((prevCheckedTripTypes) => {
      const newCheckedOptions = new Set(prevCheckedTripTypes[activeTab]);

      if (option === "All") {
        newCheckedOptions.clear();
        newCheckedOptions.add("All");
      } else {
        if (newCheckedOptions.has(option)) {
          newCheckedOptions.delete(option);
          newCheckedOptions.delete("All");
        } else {
          newCheckedOptions.add(option);
          newCheckedOptions.delete("All");
          if (
            serviceTypesOptions
              .slice(1)
              .every((opt) => newCheckedOptions.has(opt))
          ) {
            newCheckedOptions.clear();
            newCheckedOptions.add("All");
          }
        }
      }

      return {
        ...prevCheckedTripTypes,
        [activeTab]: newCheckedOptions,
      };
    });
  };

  const handleReset = () => {
    setCheckedTripTypes(defaultTripTypes);
    setTripTypes(defaultTripTypes);
    setSelectedDateRanges(defaultDateRange);
    activeTab === Tabs.UPCOMING &&
      setUpcomingDateRange(defaultUpcomingDateRange);
    activeTab === Tabs.PAST && setPastDateRange(defaultPastDateRange);
    setIsOpenFilter(false);
  };

  const handleApplyFilters = () => {
    activeTab === Tabs.UPCOMING &&
      setUpcomingDateRange(selectedDateRanges[Tabs.UPCOMING]);
    activeTab === Tabs.PAST && setPastDateRange(selectedDateRanges[Tabs.PAST]);
    setTripTypes(checkedTripTypes);
    setIsOpenFilter(false);
  };

  return {
    selectedDateRanges,
    serviceTypesOptions,
    handleTypesClick,
    handleFilterDateRange,
    isActiveTabChecked,
    handleReset,
    handleApplyFilters,
  };
};
